import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getClinicInfo, resolveMediaUrl } from '../lib/api';

export const DEFAULT_PUBLIC_LOGO = '/logo.png';

/** Título inicial de pestaña antes de cargar la API */
export const DEFAULT_PAGE_TITLE = 'Centro Cultural';

const CLINIC_INFO_CACHE_KEY = 'centrocultural_clinic_info_v1';

function readClinicInfoCache() {
  try {
    const raw = sessionStorage.getItem(CLINIC_INFO_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function writeClinicInfoCache(data) {
  try {
    if (data && typeof data === 'object') {
      sessionStorage.setItem(CLINIC_INFO_CACHE_KEY, JSON.stringify(data));
    }
  } catch {
    /* quota / private mode */
  }
}

function iconMimeFromHref(href) {
  const path = href.split(/[?#]/)[0].toLowerCase();
  if (path.endsWith('.svg')) return 'image/svg+xml';
  if (path.endsWith('.webp')) return 'image/webp';
  if (path.endsWith('.jpg') || path.endsWith('.jpeg')) return 'image/jpeg';
  if (path.endsWith('.gif')) return 'image/gif';
  return 'image/png';
}

function setBrowserTabIcon(iconHref) {
  const mime = iconMimeFromHref(iconHref);

  let icon = document.querySelector("link[rel='icon']");
  if (!icon) {
    icon = document.createElement('link');
    icon.rel = 'icon';
    document.head.appendChild(icon);
  }
  icon.href = iconHref;
  icon.type = mime;

  let shortcut = document.querySelector("link[rel='shortcut icon']");
  if (!shortcut) {
    shortcut = document.createElement('link');
    shortcut.rel = 'shortcut icon';
    document.head.appendChild(shortcut);
  }
  shortcut.href = iconHref;
  shortcut.type = mime;

  let apple = document.querySelector("link[rel='apple-touch-icon']");
  if (!apple) {
    apple = document.createElement('link');
    apple.rel = 'apple-touch-icon';
    document.head.appendChild(apple);
  }
  apple.href = iconHref;
}

const ClinicBrandingContext = createContext(null);

export function ClinicBrandingProvider({ children }) {
  const [info, setInfo] = useState(() => readClinicInfoCache());
  const [loading, setLoading] = useState(() => {
    try {
      return !sessionStorage.getItem(CLINIC_INFO_CACHE_KEY);
    } catch {
      return true;
    }
  });
  const [error, setError] = useState(null);

  const refresh = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const data = await getClinicInfo();
      setInfo(data);
      writeClinicInfoCache(data);
      setError(null);
    } catch (e) {
      setError(e);
      // Conservar info en memoria/cache si la red falla
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let hasCache = false;
    try {
      hasCache = Boolean(sessionStorage.getItem(CLINIC_INFO_CACHE_KEY));
    } catch {
      hasCache = false;
    }
    refresh({ silent: hasCache });
  }, [refresh]);

  useEffect(() => {
    const title = info?.name?.trim() ? info.name.trim() : DEFAULT_PAGE_TITLE;
    document.title = title;

    const rawLogoSrc = clinicLogo(info);
    const iconHref = resolveMediaUrl(rawLogoSrc);
    if (iconHref) {
      try {
        setBrowserTabIcon(iconHref);
      } catch {
        /* fallback: algunos navegadores bloquean data: u orígenes raros */
      }
    }
  }, [info]);

  return (
    <ClinicBrandingContext.Provider value={{ info, loading, error, refresh }}>
      {children}
    </ClinicBrandingContext.Provider>
  );
}

export function useClinicBranding() {
  const ctx = useContext(ClinicBrandingContext);
  if (!ctx) {
    throw new Error('useClinicBranding debe usarse dentro de ClinicBrandingProvider');
  }
  return ctx;
}

export function clinicLogo(info) {
  const u = info?.logo_url;
  return u && String(u).trim() !== '' ? u : DEFAULT_PUBLIC_LOGO;
}

/** True mientras no hay clinic-info (ni cache) y aún carga la API. */
export function brandingPending(loading, info) {
  return Boolean(loading && !info);
}
